import _isPlainObject from 'lodash/isPlainObject';
import AssetBlock from '@/components/settings/AssetBlock';
import EntryWhereSomeInput from '@src/components/manage/EntryWhereSomeInput';
import RichTextField from '@/components/RichTextField';
import MultiSelectField from '@/components/MultiSelectField';
import formatDate from 'date-fns/format';

const { Chrome: ChromePicker } = process.env.VUE_ENV === 'client' ? require('vue-color') : {};
const { default: DateRangePicker } = process.env.VUE_ENV === 'client' ? require('vue2-daterange-picker') : {};
const { default: validateIsEmail } = process.env.VUE_ENV === 'client' ? require('validator/es/lib/isEmail') : {};
const { default: validateIsMobilePhone } = process.env.VUE_ENV === 'client' ? require('validator/es/lib/isMobilePhone') : {};
const { default: validateIsURL } = process.env.VUE_ENV === 'client' ? require('validator/es/lib/isURL') : {};

export default {
  props: {
    layout: {
      type: String,
      default: 'blocks',
    },
    form: {
      type: Object,
      required: true,
      validate: o => Object.keys(o).filter(k => !o[k] || !o[k].type).length === 0,
    },
    value: {
      type: Object,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    tag: {
      type: [ String, Function ],
      default: null,
    },
    hideFormControl: {
      type: Boolean,
      default: false,
    },
    submitLabel: {
      type: String,
      default: 'Submit',
    },
  },
  data: () => ({
    data: null,
    update: null,
  }),
  created() {
    this.data = this.value ? JSON.parse(JSON.stringify(this.value)) : {};
    this.update = {};
  },
  computed: {
    /**
     * @type {Object<string,string|boolean|null>}
     */
    valid() {
      const valid = {};

      for (const key in this.form) {
        if (this.form.hasOwnProperty(key) && this.form[key].type) {
          const { type, required } = this.form[key];

          if (this.data && this.data.hasOwnProperty(key)) {
            const { [key]: value } = this.data;
            try {
              this.$assert(!required || type === 'LABEL' || (value !== null && value !== undefined && value !== ''), 'This field is required', { code: 'MISSING_REQUIRED_VALUE' });

              if (value) {
                // Run custom validators if a value has been set
                if (typeof validation[type] === 'function') {
                  validation[type].call(this, value, key, this.form[key]);
                }

                // Otherwise, this value is "valid"
                valid[key] = true;
              } else {
                // In this state, no value has no state, but is technically "valid"
                valid[key] = null;
              }
            } catch (err) {
              Object.assign(err, { form: this.form, data: this.data, key, value });
              this.$log.error({ ...err }, err);

              // A string "valid" value is an error!
              valid[key] = this.$te(`advanced-form.errors.${err.code}`)
                ? this.$t(`advanced-form.errors.${err.code}`, { ...err })
                : this.$t('general.errored');
            }
          } else {
            valid[key] = null;
          }
        }
      }

      return valid;
    },
  },
  methods: {
    setData(key, value) {
      this.$assert(typeof key === 'string', new TypeError('Expected setData key to be a string'));

      const before = JSON.stringify(this.data[key]);
      const after = JSON.stringify(value);

      if (before !== after) {
        value = JSON.parse(after);

        this.$set(this.data, key, value);
        this.$set(this.update, key, value);

        this.$emit('input', JSON.parse(JSON.stringify(this.data)));
        this.$emit('update', JSON.parse(JSON.stringify(this.update)));

        return true;
      } else {
        return false;
      }
    },
    submit() {
      // Set empty properties for untouched form values
      this.data = {
        ...Object.keys(this.form).reduce((c, k) => ({ ...c, [k]: undefined }), {}),
        ...this.data,
      };

      const isValid = Object.keys(this.valid).reduce((result, key) => result &&
        (this.valid[key] === true || this.valid[key] === null), true);
      this.$log.debug({ data: this.data, valid: this.valid, isValid });

      if (isValid) {
        this.$emit('submit', this.data);
      }
    },
  },
  render(el) {
    const defaultGroup = {
      props: {},
    };
    switch (typeof this.layout === 'string' ? this.layout : null) {
      case 'inline-md-3': defaultGroup.props.labelColsMd = 3; break;
      case 'inline-md-4': defaultGroup.props.labelColsMd = 4; break;
      case 'inline-md-6': defaultGroup.props.labelColsMd = 6; break;
    }

    const groups = Object.keys(this.form).reduce((gs, id) => {
      const { [id]: field } = this.form;
      const children = [];
      const state = this.valid[id] === true
        ? (field.required === true ? true : null)
        : (typeof this.valid[id] === 'string' ? false : null);

      const group = {
        ...defaultGroup,
        props: {
          ...defaultGroup.props,
          label: field.label,
          labelFor: id,
        },
      };

      if (group.props.label && field.required === true) {
        group.props.label += '*';
      }

      const {
        [`${id}-label`]: labelSlot,
        [`${id}-valid-feedback`]: validFeedbackSlot,
        [`${id}-invalid-feedback`]: invalidFeedbackSlot,
      } = this.$scopedSlots;
      if (typeof labelSlot === 'function') {
        group.scopedSlots = { ...group.scopedSlots, label: labelSlot };
      }
      if (typeof validFeedbackSlot === 'function') {
        group.scopedSlots = { ...group.scopedSlots, 'valid-feedback': validFeedbackSlot };
      }
      if (typeof invalidFeedbackSlot === 'function') {
        group.scopedSlots = { ...group.scopedSlots, 'invalid-feedback': invalidFeedbackSlot };
      }

      switch (field.type) {

        case 'STRING': case 'NUMBER': {
          const { autocomplete } = field.options || {};
          const { isEmailAddress } = field.validation || {};

          let type = 'text';
          if (field.type === 'NUMBER') {
            type = 'number';
          } else if (isEmailAddress === true) {
            type = 'email';
          }

          if (!this.data[id] && field.defaultValue) {
            this.setData(id, field.defaultValue);
          }

          children.push(el('b-form-input', {
            props: {
              autocomplete,
              disabled: field.disabled || this.disabled || false,
              placeholder: field.placeholder,
              state,
              type,
              value: this.data[id],
            },
            attrs: { id },
            on: { input: value => this.setData(id, typeof value === 'string' && value.length ? value : null) },
          }));
          break;
        }

        case 'TEXTAREA': {
          children.push(el('b-form-textarea', {
            props: {
              disabled: field.disabled || this.disabled || false,
              placeholder: field.placeholder,
              state,
              rows: field.rows,
              value: this.data[id],
            },
            attrs: { id },
            on: { input: value => this.setData(id, typeof value === 'string' && value.length ? value : null) },
          }));
          break;
        }

        case 'HTML': {
          const { extensions } = field.options || {};
          children.push(el(RichTextField, {
            props: {
              disabled: field.disabled || this.disabled || false,
              extensions: Array.isArray(extensions) ? extensions : `${extensions}`.split(',').map(s => s.trim()),
              placeholder: field.placeholder,
              // state,
              value: this.data[id],
            },
            attrs: { id },
            on: { input: value => this.setData(id, typeof value === 'string' && value.length ? value : null) },
          }));
          break;
        }

        case 'LABEL': {
          children.push(el('label', {
            props: {
              disabled: field.disabled || this.disabled || false,
            },
            attrs: { id },
          }));
          break;
        }

        case 'MULTISELECT': {
          const options = createOptions(field.choices);

          children.push(el(MultiSelectField, {
            props: {
              disabled: field.disabled || this.disabled || false,
              placeholder: field.placeholder,
              options,
              value: this.data[id],
            },
            attrs: { id },
            on: { input: value => this.setData(id, Array.isArray(value) && value.length ? value : null) },
          }));
          break;
        }

        case 'SELECT': {
          const options = createOptions(field.choices);
          if (field.placeholder) {
            options.unshift({ text: field.placeholder, value: null });
          }

          children.push(el('b-form-select', {
            props: {
              disabled: field.disabled || this.disabled || false,
              placeholder: field.placeholder,
              options,
              state,
              value: this.data[id],
            },
            attrs: { id },
            on: { input: value => this.setData(id, typeof value === 'string' && value.length ? value : null) },
          }));
          break;
        }

        case 'CHECKBOX': {
          const { [`${id}-description`]: descriptionSlot } = this.$scopedSlots;
          const { defaultValue = null } = field.options || {};

          children.push(el('b-form-checkbox', {
            props: {
              checked: typeof this.data[id] === 'boolean' ? this.data[id] : Boolean(defaultValue),
              disabled: field.disabled || this.disabled || false,
              state,
              switch: true,
            },
            attrs: { id },
            on: { input: value => this.setData(id, typeof value === 'boolean' ? value : null) },
          }, [ typeof descriptionSlot === 'function' ? descriptionSlot() : field.description ]));

          break;
        }

        case 'CHECKBOX_GROUP': {
          const { defaultValue = null } = field.options || {};

          const defaults = typeof defaultValue === 'string' ? defaultValue.split(',').map(s => s.trim()) : [];
          const options = createOptions(field.choices);

          children.push(el('b-form-checkbox-group', {
            props: {
              checked: Array.isArray(this.data[id]) ? this.data[id] : defaults,
              disabled: field.disabled || this.disabled || false,
              options,
              state,
              switches: true,
            },
            attrs: { id },
            on: { input: value => this.setData(id, Array.isArray(value) ? value : null) },
          }));
          break;
        }

        case 'RADIO_GROUP': {
          const options = [];

          for (const key in field.choices) {
            if (field.choices.hasOwnProperty(key)) {
              options.push({ text: field.choices[key], value: key });
            }
          }

          children.push(el('b-form-radio-group', {
            props: {
              checked: this.data[id],
              disabled: field.disabled || this.disabled || false,
              options,
              state,
              switches: true,
            },
            attrs: { id },
            on: { input: value => this.setData(id, value) },
          }));
          break;
        }

        case 'DATE': {
          children.push(el('b-form-datepicker', {
            props: {
              disabled: field.disabled || this.disabled || false,
              hideHeader: true,
              placeholder: field.placeholder,
              startWeekday: 1,
              state,
              value: this.data[id],
            },
            attrs: { id },
            on: { input: value => this.setData(id, typeof value === 'string' && value.length ? value : null) },
          }));
          break;
        }

        case 'DATETIME': {
          const { format } = field.options || {};
          const defaultFormat = 'YYYY-MM-DD HH:mm';
          children.push(el(DateRangePicker, {
            props: {
              dateRange: { startDate: this.data[id], endDate: this.data[id] },
              disabled: field.disabled || this.disabled || false,
              format: format || defaultFormat,
              lang: 'en',
              opens: 'right',
              ranges: false,
              timePicker: true,
              timePicker24Hour: true,
              singleDatePicker: true,
            },
            class: 'w-100',
            attrs: { id },
            scopedSlots: {
              input: dates => dates && dates.startDate
                ? formatDate(dates.startDate, format || defaultFormat)
                : el('span', { class: 'text-muted' }, field.placeholder),
              footer: ({ clickApply, clickCancel }) => el('div', {
                class: 'd-flex flex-row border-top p-1',
              }, [
                el('b-btn', {
                  props: { size: 'sm', variant: 'primary' },
                  on: { click: clickApply },
                }, 'Save'),
                el('b-btn', {
                  props: { size: 'sm', variant: 'light' },
                  on: { click: () => { this.setData(id, null); clickCancel(); } },
                }, 'Clear'),
              ]),
            },
            on: {
              update: range => {
                if (range && range.hasOwnProperty('startDate')) {
                  const { startDate } = range;
                  this.setData(id, startDate.toISOString());
                }
              },
            },
          }));
          break;
        }

        case 'FILE': {
          const { accept, type, showPreview } = field.options || {};

          children.push(el(AssetBlock, {
            props: {
              accept,
              disabled: field.disabled || this.disabled || false,
              field: { type },
              placeholder: field.placeholder,
              showPreview: showPreview === true,
              state,
              value: this.data[id],
            },
            class: 'm-0',
            attrs: { id },
            on: { input: asset => this.setData(id, asset && asset.id ? asset : null) },
          }));
          break;
        }

        case 'RELATION': {
          const { type, title, many = false, where = { status: 'ACTIVE' }, order } = field.options || {};
          this.$assert(typeof type === 'string', 'Missing { type } from relation field options');
          this.$assert(typeof title === 'string', 'Missing { title } from relation field options');

          children.push(el(EntryWhereSomeInput, {
            props: {
              type,
              title,
              many: many === true,
              where: { ...where },
              order,
              value: this.data[id],
            },
            attrs: { id },
            on: { input: value => this.setData(id, value || null) },
          }));
          break;
        }

        case 'COLOR-PICKER': {
          const { defaultValue } = field.options || {};
          children.push(el(ChromePicker, {
            props: {
              disableAlpha: true,
              value: this.data[id] || defaultValue || '#ffffff',
            },
            attrs: { id },
            on: { input: ({ hex }) => this.setData(id, hex || null) },
          }));
          break;
        }

      }

      const { [`${id}-helpText`]: helpTextSlot } = this.$scopedSlots;
      if (helpTextSlot) {
        children.push(helpTextSlot());
      } else if (field.helpText) {
        children.push(el('b-form-text', {}, [ field.helpText ]));
      }

      if (typeof this.valid[id] === 'string') {
        children.push(el('div', { class: 'invalid-feedback d-inline' }, [ this.valid[id] ]));
      }

      const {
        [`${id}-before`]: beforeSlot,
        [`${id}-after`]: afterSlot,
        [`${id}-input-group-prepend`]: inputGroupPrependSlot,
        [`${id}-input-group-append`]: inputGroupAppendSlot,
      } = this.$scopedSlots;

      if (inputGroupPrependSlot || inputGroupAppendSlot) {
        return gs.concat(...[
          typeof beforeSlot === 'function' ? beforeSlot() : [],
          children.length ? el('b-form-group', group, [
            el('b-input-group', {
              scopedSlots: { prepend: inputGroupPrependSlot, append: inputGroupAppendSlot },
            }, children),
          ]) : [],
          typeof afterSlot === 'function' ? afterSlot() : [],
        ]);
      } else {
        return gs.concat(...[
          typeof beforeSlot === 'function' ? beforeSlot() : [],
          children.length ? el('b-form-group', group, children) : [],
          typeof afterSlot === 'function' ? afterSlot() : [],
        ]);
      }
    }, []);

    if (!this.hideFormControl) {
      const {
        formControl: formControlSlot,
        'formControl-before': formControlBeforeSlot,
        'formControl-after': formControlAfterSlot,
      } = this.$scopedSlots;
      groups.push(typeof formControlSlot === 'function'
        ? formControlSlot({
          onSubmit: () => this.submit(),
          submitLabel: () => this.submitLabel,
        })
        : el('b-form-group', defaultGroup, [
          typeof formControlBeforeSlot === 'function' ? formControlBeforeSlot() : null,
          el('b-btn', {
            props: { type: 'submit', variant: 'primary' },
            on: this.tag ? { click: () => this.submit() } : {},
          }, [ this.submitLabel || 'Submit' ]),
          typeof formControlAfterSlot === 'function' ? formControlAfterSlot() : null,
        ]));
    }

    const { before: globalBeforeSlot, after: globalAfterSlot } = this.$scopedSlots;
    if (typeof globalBeforeSlot === 'function') {
      groups.unshift(globalBeforeSlot());
    }
    if (typeof globalAfterSlot === 'function') {
      groups.push(globalAfterSlot());
    }

    return this.tag
      ? el(this.tag, {}, groups)
      : el('b-form', {
        attrs: {
          method: 'POST',
        },
        on: {
          submit: e => {
            e.preventDefault();
            this.submit();
          },
        },
      }, groups);
  },
};

const validation = {
  STRING(value, key, field) {
    const { isEmailAddress, isPhoneNumber, isURL, minLength, minWords } = { ...field.validation };
    if (isEmailAddress === true) {
      this.$assert(typeof value === 'string', 'Expected value to be a string', {
        code: 'TYPE_MISMATCH',
      });
      this.$assert(validateIsEmail(value), 'Expected value to be an email address', {
        code: 'STRING_IS_NOT_EMAILADDRESS',
      });
    }

    if (isPhoneNumber === true) {
      this.$assert(typeof value === 'string', 'Expected value to be a string', {
        code: 'TYPE_MISMATCH',
      });
      this.$assert(validateIsMobilePhone(value), 'Expected value to be an phone number', {
        code: 'STRING_IS_NOT_PHONENUMBER',
      });
    }

    if (isURL === true) {
      this.$assert(typeof value === 'string', 'Expected value to be a string', {
        code: 'TYPE_MISMATCH',
      });
      this.$assert(validateIsURL(value), 'Expected value to be an phone number', {
        code: 'STRING_IS_NOT_URL',
      });
    }

    if (typeof minLength === 'number') {
      this.$assert(`${typeof value === 'string' ? value : ''}`.length >= minLength, 'Expected value to be longer than', {
        code: 'STRING_IS_LESS_THAN_MINLENGTH',
        minLength,
      });
    }

    if (typeof minWords === 'number') {
      this.$assert(`${typeof value === 'string' ? value : ''}`.trim().split(' ').length >= minWords, 'Expected value to be longer than', {
        code: 'STRING_IS_LESS_THAN_MINWORDS',
        minWords,
        count: minWords,
      });
    }
  },
  TEXTAREA(value, key, field) {
    const { minLength } = { ...field.validation };

    if (typeof minLength === 'number') {
      this.$assert(`${typeof value === 'string' ? value : ''}`.length >= minLength, 'Expected value to be longer than', {
        code: 'STRING_IS_LESS_THAN_MINLENGTH',
        minLength,
      });
    }
  },
  DATE(value, key, field) {
    const { isBefore, isAfter } = { ...field.validation };

    if (typeof isBefore === 'string') {
      this.$assert(value <= new Date(isBefore), 'Expected value to be before the date', {
        code: 'DATE_IS_NOT_BEFORE',
        target: isBefore,
      });
    }

    if (typeof isAfter === 'string') {
      this.$assert(value >= new Date(isAfter), 'Expected value to be after the date', {
        code: 'DATE_IS_NOT_AFTER',
        target: isAfter,
      });
    }
  },
  DATETIME(value, key, field) {
    const { isBefore, isAfter } = { ...field.validation };

    if (typeof isBefore === 'string') {
      this.$assert(value <= new Date(isBefore), 'Expected value to be before the date', {
        code: 'DATE_IS_NOT_BEFORE',
        target: isBefore,
      });
    }

    if (typeof isAfter === 'string') {
      this.$assert(value >= new Date(isAfter), 'Expected value to be after the date', {
        code: 'DATE_IS_NOT_AFTER',
        target: isAfter,
      });
    }
  },
};

function createOptions(choices) {
  const sorter = (a, b) => {
    if (a.text && b.text) {
      return a.text.localeCompare(b.text, 'en', { numeric: true });
    } else {
      return a.localeCompare(b, 'en', { numeric: true });
    }
  };

  if (_isPlainObject(choices)) {
    return Object
      .keys(choices)
      .map(value => {
        const { [value]: text } = choices;
        return { text, value };
      })
      .sort(sorter);
  } else if (Array.isArray(choices)) {
    return choices
      .slice()
      .sort(sorter);
  } else {
    return null;
  }
}



// WEBPACK FOOTER //
// ./src/components/AdvancedForm.js